import { useRef, useState, useEffect } from 'react';

type InputsBase = readonly unknown[];

interface ICache<T, InputsType extends InputsBase> {
  inputs?: InputsType;
  result: T;
}

type IsEqualType<T> = (a: T, b: T) => boolean;

/**
 * Version of `useMemo` that provides semantic guarantees.
 *
 * Based on https://github.com/alexreardon/use-memo-one, but with the optional
 * `isEqual` argument added.
 */
export function useMemoOne<T, InputsType extends InputsBase>(
  getResult: () => T,
  inputs: InputsType,
  isEqual?: IsEqualType<InputsType[number]>,
): T {
  // using useState to generate initial value as it is lazy
  const [initial] = useState<ICache<T, InputsType>>(() => ({
    inputs,
    result: getResult(),
  }));

  const committed = useRef<ICache<T, InputsType>>(initial);

  // persist any uncommitted changes after they have been committed

  const isInputMatch = Boolean(
    inputs &&
      committed.current.inputs &&
      areInputsEqual(inputs, committed.current.inputs, isEqual),
  );

  // create a new cache if required
  // It is desired to change the cache key in order to trigger the effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cache: ICache<T, InputsType> = isInputMatch
    ? committed.current
    : {
      inputs,
      result: getResult(),
    };

  // commit the cache
  useEffect(() => {
    committed.current = cache;
  }, [cache]);

  return cache.result;
}

export function useCallbackOne<T extends(...args: any[]) => unknown, InputsType extends InputsBase>(
  callback: T,
  inputs: InputsType,
): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemoOne(() => callback, inputs);
}

function referrentiallyEqual(a: unknown, b: unknown): boolean {
  return a === b;
}

function areInputsEqual<InputsType extends InputsBase>(
  newInputs: InputsType,
  lastInputs: InputsType,
  isEqual: IsEqualType<InputsType[number]> = referrentiallyEqual,
) {
  // no checks needed if the inputs length has changed
  if (newInputs.length !== lastInputs.length) {
    return false;
  }
  // Using for loop for speed. It generally performs better than array.every
  // https://github.com/alexreardon/memoize-one/pull/59

  for (let i = 0; i < newInputs.length; i++) {
    // using shallow equality check
    if (!isEqual(newInputs[i], lastInputs[i])) {
      return false;
    }
  }
  return true;
}
